import React, { useEffect, useRef, useState } from 'react';
import TopNavBar from '../../features/top-nav-bar/top-nav-bar';
import Typography, {
  TypographyVariant,
} from '../../components/typography/typography';
import Grid from '../../components/grid/grid';
import RuleOfThumbInputs, {
  RuleOfThumbInputsData,
} from './rule-of-thumb-inputs';
import RuleOfThumbResults from './rule-of-thumb-results';

function InvestorRuleOfThumb() {
  const topNavBarRef = useRef(null);
  const [topNavBarHeight, setTopNavBarHeight] = useState(0);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const [infoCardHeight, setInfoCardHeight] = useState(0);
  const infoCardRef = useRef<HTMLDivElement | null>(null);
  const [inputData, setInputData] = useState<RuleOfThumbInputsData | null>(
    null
  );

  // get height of top nav bar and info card
  useEffect(() => {
    window.scrollTo(0, 0);

    const measureHeights = () => {
      if (topNavBarRef.current) {
        setTopNavBarHeight((topNavBarRef.current as HTMLElement).offsetHeight);
      }
      if (infoCardRef.current) {
        setInfoCardHeight(infoCardRef.current.offsetHeight);
      }
    };

    measureHeights();
    window.addEventListener('resize', measureHeights);
    return () => window.removeEventListener('resize', measureHeights);
  }, []);

  // Check screen width on mount and resize
  useEffect(() => {
    const checkScreenWidth = () => {
      setIsNarrowScreen(window.innerWidth < 768); // Show message below 768px
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);

    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);

  const availableHeight = `calc(100vh - ${
    topNavBarHeight + infoCardHeight + 24
  }px)`;

  const handleInputChange = (data: RuleOfThumbInputsData) => {
    setInputData(data);
  };

  return (
    <div
      style={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
      className="bg-gradient-hero"
    >
      <TopNavBar ref={topNavBarRef} setMaxWidth={true} />
      {/* Info Card */}
      <div
        ref={infoCardRef}
        className="flex items-center justify-center"
        // style={{ paddingBottom: '8px' }}
      >
        <div
          className="glass-panel p-8 text-center max-w-4xl"
          style={{
            padding: '24px',
            margin: '16px',
            marginTop: '16px',
            marginBottom: '0px',
          }}
        >
          <Typography
            variant={TypographyVariant.Subtitle1}
            className="text-secondary"
            style={{ lineHeight: '1.6', textAlign: 'left' }}
          >
            The <strong>Break-Even Rent Rule </strong>
            gives you one quick number — the monthly rent percentage of a
            property’s value needed to cover typical expenses. Multiply the
            asking price by this percentage; if the rent seems realistic,
            investigate further. If not, move on.
          </Typography>
        </div>
      </div>

      {/* Main Content */}
      {isNarrowScreen ? (
        <div
          className="flex items-center justify-center p-4"
          style={{ height: `calc(${availableHeight})` }}
        >
          <div
            className="glass-panel p-8 text-center max-w-md"
            style={{ padding: '16px', margin: '16px 16px' }}
          >
            <Typography
              variant={TypographyVariant.H5}
              className="text-primary mb-3"
            >
              Screen Too Small
            </Typography>
            <Typography
              variant={TypographyVariant.Subtitle2}
              className="text-secondary"
            >
              We currently only support desktop view for the Rule of Thumb
              calculator. Please view on a larger screen (minimum width: 768px)
              for the best experience.
            </Typography>
          </div>
        </div>
      ) : (
        <Grid container style={{ height: availableHeight }}>
          {/* Left side - Inputs */}
          <Grid
            item
            sm={5}
            md={4}
            lg={2.5}
            style={{
              padding: '1rem',
              paddingBottom: '2rem',
            }}
          >
            <RuleOfThumbInputs onInputChange={handleInputChange} />
          </Grid>

          {/* Right side - Graphics */}
          <Grid
            item
            sm={7}
            md={8}
            lg={9.5}
            style={{
              padding: '1rem',
              paddingBottom: '2rem',
            }}
          >
            <RuleOfThumbResults inputData={inputData} />
          </Grid>
        </Grid>
      )}
    </div>
  );
}

export default InvestorRuleOfThumb;
